export const GUIDED_COHORT_LANGUAGES = [
  { slug: "french", name: "French", languageCode: "fr-FR" },
  { slug: "dutch", name: "Dutch", languageCode: "nl-NL" },
  { slug: "spanish", name: "Spanish", languageCode: "es-ES" },
  { slug: "greek", name: "Greek", languageCode: "el-GR" },
] as const;

export const GUIDED_COHORT_COURSE_FORMAT = {
  dailyPracticeMinutes: 30,
  durationWeeks: 2,
  groupLabel: "Small group",
  levelLabel: "A1/A2 beginner",
  liveClassCount: 6,
  modalityLabel: "Online",
  totalGuidedHours: 13,
} as const;

export type GuidedCohortLanguageSlug = (typeof GUIDED_COHORT_LANGUAGES)[number]["slug"];
export type GuidedCohortFormat = "online";

export type RollingCohortWindow = {
  targetStartMonth: string;
  targetStartLabel: string;
  cutoffDay: number;
};

const ENROLLMENT_CUTOFF_DAY = 15;
const COHORT_TIME_ZONE = "Europe/Amsterdam";

function getAmsterdamCalendarDate(now: Date) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    day: "2-digit",
    month: "2-digit",
    timeZone: COHORT_TIME_ZONE,
    year: "numeric",
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));

  return {
    day: Number(values.day),
    month: Number(values.month),
    year: Number(values.year),
  };
}

export function getRollingCohortWindow(now = new Date()): RollingCohortWindow {
  const current = getAmsterdamCalendarDate(now);
  const monthsAhead = current.day <= ENROLLMENT_CUTOFF_DAY ? 1 : 2;
  const targetMonth = new Date(Date.UTC(current.year, current.month - 1 + monthsAhead, 1));

  return {
    targetStartMonth: targetMonth.toISOString().slice(0, 10),
    targetStartLabel: new Intl.DateTimeFormat("en-GB", {
      month: "long",
      timeZone: "UTC",
      year: "numeric",
    }).format(targetMonth),
    cutoffDay: ENROLLMENT_CUTOFF_DAY,
  };
}

export function getGuidedCohortLanguageFromCode(
  languageCode: string | null | undefined
): GuidedCohortLanguageSlug {
  const normalized = languageCode?.trim().toLowerCase() ?? "";

  if (normalized.startsWith("nl")) return "dutch";
  if (normalized.startsWith("es")) return "spanish";
  if (normalized.startsWith("el")) return "greek";
  return "french";
}

export function getGuidedCohortLanguageName(languageSlug: GuidedCohortLanguageSlug) {
  return (
    GUIDED_COHORT_LANGUAGES.find((language) => language.slug === languageSlug)?.name ?? "French"
  );
}
